import React, { useMemo } from 'react';
import { TabState, DataValue, EphemeralDataSet, createDatasetId } from '../../../../types';
import { DataTableView } from '../../DataTableView';
import { useProjectData } from "../../../stores/hooks/useProjectData"
import { useDatasetSelection } from "../../../stores/hooks/useDatasetSelection"
import { usePaneLayout } from "../../../stores/hooks/usePaneLayout"
import { useDatasetWithData } from "../../../hooks/useDatasetWithData"
import { findActiveDatasetIdForTab } from '../../../utils/activeDatasetUtils';
import * as styles from '../../../tab-system/TabContainer.css.ts';

interface SelectedRowsTabContentProps {
  tab: TabState;
}

export const SelectedRowsTabContent: React.FC<SelectedRowsTabContentProps> = ({ tab }) => {
  
  // Context APIから必要なデータを取得
  const { getDataset } = useProjectData();
  const { layout } = usePaneLayout();
  
  // アクティブなデータセットIDを取得（共通ユーティリティを使用）
  const activeDatasetId = findActiveDatasetIdForTab(layout, tab.id) || undefined;
  
  const tabDataset = activeDatasetId ? getDataset(activeDatasetId) : undefined;
  
  // データセットのデータを自動的にロード
  const { accessor, isLoading, isDataAvailable } = useDatasetWithData(tabDataset);
  
  // DataTableSelectionContextから選択状態を取得
  const { selectedRows } = useDatasetSelection(activeDatasetId || undefined);
  
  // デバッグログ

  // 選択された行のデータのみを含む一時的なデータセットを作成
  // 早期リターンによりこのuseMemoの結果が使われるのは、すべての条件を満たす場合のみ
  const selectedRowsDataset = useMemo<EphemeralDataSet>(() => {
    // 早期リターンにより実際には使われないが、Hooks のルールにより何か返す必要がある
    if (!tabDataset || !accessor || selectedRows.length === 0 || !isDataAvailable) {
      // 最小限のダミーデータセット
      return { id: createDatasetId(''), name: '', type: 'ephemeral', data: {}, schema: { columns: [] }, rowCount: 0, metadata: { createdAt: '', purpose: 'selected_rows' } } as EphemeralDataSet;
    }


    // 選択された行のデータを抽出
    const filteredData: Record<string, DataValue[]> = {};
    
    // すべての列に対してデータを抽出
    for (const column of tabDataset.schema.columns) {
      const columnData = accessor.getColumnDataSync(column.id);
      if (columnData) {
        filteredData[column.id] = selectedRows.map(rowIndex => columnData[rowIndex]);
      }
    }

    // EphemeralDataSet を作成
    const result: EphemeralDataSet = {
      id: createDatasetId(`dataset-ephemeral-${crypto.randomUUID()}`),
      name: `Selected Rows from ${tabDataset.name}`,
      type: 'ephemeral',
      data: filteredData,
      schema: {
        ...tabDataset.schema
      },
      rowCount: selectedRows.length,
      metadata: {
        sourceDatasetId: tabDataset.id,
        createdAt: new Date().toISOString(),
        purpose: 'selected_rows',
        description: `${selectedRows.length} rows selected from ${tabDataset.name}`
      }
    };


    return result;
  }, [tabDataset, selectedRows, accessor, isDataAvailable]);

  // データセットが無い場合はプレースホルダーを表示
  if (!tabDataset) {
    return (
      <div data-testid="selected-rows-content" className={styles.tabContentScrollable}>
        <div className={styles.selectedRowsTabContent}>
          <p>No dataset loaded. Please open a data table first.</p>
        </div>
      </div>
    );
  }

  // データのロード中
  if (isLoading) {
    return (
      <div data-testid="selected-rows-content" className={styles.tabContentScrollable}>
        <div className={styles.selectedRowsTabContent}>
          <p>Loading dataset "{tabDataset.name}"...</p>
        </div>
      </div>
    );
  }
  
  // データが利用できない場合
  if (!isDataAvailable) {
    return (
      <div data-testid="selected-rows-content" className={styles.tabContentScrollable}>
        <div className={styles.selectedRowsTabContent}>
          <p>Dataset "{tabDataset.name}" has no data available.</p>
        </div>
      </div>
    );
  }

  // 選択された行が無い場合
  if (selectedRows.length === 0) {
    return (
      <div data-testid="selected-rows-content" className={styles.tabContentScrollable}>
        <div className={styles.selectedRowsTabContent}>
          <div className={styles.emptyState}>
            <h3>No Rows Selected</h3>
            <p>Select rows in the data table to view them here.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="selected-rows-content" className={styles.tabContentScrollable} style={{ padding: 0 }}>
      <DataTableView dataset={selectedRowsDataset} />
    </div>
  );
};